import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SetupChecklistTab, { useChecklistSteps } from './sections/SetupChecklistTab';
import ProfilingTab from './sections/ProfilingTab';
import UpdatesTab from './sections/UpdatesTab';

const GetSetUpCard = ({
  agentProfile,
  userData,
  stats,
  recentVouchers = [],
  hotelInputRef,
  isSideBySide,
  leftCardHeight,
  agentUpdatesEnabled = true,
  isVoucherLocked,
  onOpenUpdatesModal
}) => {
  const navigate = useNavigate();

  // 3-Tab Card State: 0 = Checklist ("Get set up"), 1 = Profiling ("Business Profile"), 2 = Updates ("What's New")
  const [activeCardTab, setActiveCardTab] = useState(0);

  const [hasCompletedProfiling, setHasCompletedProfiling] = useState(
    Boolean(agentProfile?.profiling_answers && agentProfile.profiling_answers.length > 0)
  );

  useEffect(() => {
    if (agentProfile?.profiling_answers && agentProfile.profiling_answers.length > 0) {
      setHasCompletedProfiling(true);
    }
  }, [agentProfile?.profiling_answers]);

  const {
    steps,
    completedCount,
    progressPercentage,
    isAllCompleted
  } = useChecklistSteps({
    userData,
    agentProfile,
    stats,
    recentVouchers,
    hotelInputRef,
    isVoucherLocked,
    navigate
  });

  const showSetupTab = !isAllCompleted;
  const showProfileTab = !hasCompletedProfiling;
  const showUpdatesTab = Boolean(agentUpdatesEnabled);

  // Adjust active card tab if current tab has been completed / hidden
  useEffect(() => {
    if (activeCardTab === 0 && !showSetupTab) {
      if (showProfileTab) {
        setActiveCardTab(1);
      } else if (showUpdatesTab) {
        setActiveCardTab(2);
      }
    } else if (activeCardTab === 1 && !showProfileTab) {
      if (showSetupTab) {
        setActiveCardTab(0);
      } else if (showUpdatesTab) {
        setActiveCardTab(2);
      }
    } else if (activeCardTab === 2 && !showUpdatesTab) {
      if (showSetupTab) {
        setActiveCardTab(0);
      } else if (showProfileTab) {
        setActiveCardTab(1);
      }
    }
  }, [showSetupTab, showProfileTab, showUpdatesTab, activeCardTab]);

  return (
    <div
      style={
        isSideBySide
          ? {
              width: '380px',
              minWidth: '380px',
              maxWidth: '380px',
              height: leftCardHeight ? `${leftCardHeight}px` : undefined
            }
          : undefined
      }
      className={`${
        isSideBySide ? 'w-[380px] min-w-[380px] max-w-[380px] shrink-0' : 'w-full'
      } bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 border border-slate-100 shadow-sm flex flex-col hover:shadow-md transition-all duration-300 overflow-hidden`}
    >
      {/* Top Header: Title on Left + Primary Tab Pills on Right */}
      <div className="shrink-0 flex items-center justify-between pb-2 sm:pb-2.5 border-b border-slate-100 mb-2 gap-1.5">
        <h2 className="text-base sm:text-lg font-normal text-slate-800 tracking-tight shrink-0 whitespace-nowrap">
          {activeCardTab === 0 && 'Get set up'}
          {activeCardTab === 1 && 'Questions'}
          {activeCardTab === 2 && "What's New"}
        </h2>

        <div className="flex items-center gap-0.5 sm:gap-1 p-0.5 bg-slate-100/80 rounded-xl shrink-0">
          {showSetupTab && (
            <button
              type="button"
              onClick={() => setActiveCardTab(0)}
              className={`px-2 sm:px-2.5 py-1 rounded-lg text-[11px] sm:text-xs font-normal transition-all flex items-center gap-1 cursor-pointer whitespace-nowrap ${
                activeCardTab === 0
                  ? 'bg-primary text-white shadow-2xs border border-primary font-normal'
                  : 'text-slate-500 hover:text-slate-800 font-normal'
              }`}
            >
              <span>Setup</span>
            </button>
          )}

          {showProfileTab && (
            <button
              type="button"
              onClick={() => setActiveCardTab(1)}
              className={`px-2 sm:px-2.5 py-1 rounded-lg text-[11px] sm:text-xs font-normal transition-all flex items-center gap-1 cursor-pointer whitespace-nowrap ${
                activeCardTab === 1
                  ? 'bg-primary text-white shadow-2xs border border-primary font-normal'
                  : 'text-slate-500 hover:text-slate-800 font-normal'
              }`}
            >
              <span>Profile</span>
            </button>
          )}

          {showUpdatesTab && (
            <button
              type="button"
              onClick={() => setActiveCardTab(2)}
              className={`px-2 sm:px-2.5 py-1 rounded-lg text-[11px] sm:text-xs font-normal transition-all flex items-center gap-1 cursor-pointer whitespace-nowrap ${
                activeCardTab === 2
                  ? 'bg-primary text-white shadow-2xs border border-primary font-normal'
                  : 'text-slate-500 hover:text-slate-800 font-normal'
              }`}
            >
              <span>Updates</span>
              <span className={`w-1.5 h-1.5 rounded-full ${activeCardTab === 2 ? 'bg-white' : 'bg-primary'} animate-pulse`}></span>
            </button>
          )}
        </div>
      </div>

      {/* Middle Dynamic Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* TAB 1: GET SET UP CHECKLIST */}
        {activeCardTab === 0 && !isAllCompleted && (
          <SetupChecklistTab
            steps={steps}
            completedCount={completedCount}
            progressPercentage={progressPercentage}
            isSideBySide={isSideBySide}
          />
        )}

        {/* TAB 2: EMBEDDED PROFILING QUESTIONS */}
        {activeCardTab === 1 && !hasCompletedProfiling && (
          <ProfilingTab
            agentProfile={agentProfile}
            userData={userData}
            hasCompletedProfiling={hasCompletedProfiling}
            setHasCompletedProfiling={setHasCompletedProfiling}
            onProfileSaved={() => setActiveCardTab(0)}
          />
        )}

        {/* TAB 3: WHAT'S NEW & PRODUCT UPDATES */}
        {activeCardTab === 2 && showUpdatesTab && (
          <UpdatesTab onOpenUpdatesModal={onOpenUpdatesModal} />
        )}
      </div>
    </div>
  );
};

export default GetSetUpCard;
